import { useState, useEffect, useRef } from "react"
import { Check, Copy } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Alert,
  AlertDescription,
} from "@f2e/ui/components/base"

import type { WorkspaceApiKeyRole } from "@/types"

export interface ApiKeyCreatedDialogProps {
  open: boolean
  apiKey: string
  apiKeyName: string
  role: WorkspaceApiKeyRole
  onOpenChange: (open: boolean) => void
}

export const ApiKeyCreatedDialog = ({
  open,
  apiKey,
  apiKeyName,
  role,
  onOpenChange,
}: ApiKeyCreatedDialogProps) => {
  const { t } = useTranslation()

  const [copied, setCopied] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (open && textareaRef.current) {
      // Auto-select the API key text when dialog opens
      textareaRef.current.select()
    }
    // Reset copied state when dialog opens
    if (open) {
      setCopied(false)
    }
  }, [open])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error("Failed to copy:", err)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>
            {t("workspace.apiKeys.createdDialog.title")}
          </DialogTitle>
          <DialogDescription>
            {t("workspace.apiKeys.createdDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert
            variant="default"
            className="border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950"
          >
            <AlertDescription className="text-yellow-800 dark:text-yellow-200">
              <strong>{t("workspace.apiKeys.createdDialog.warning")}</strong>{" "}
              {t("workspace.apiKeys.createdDialog.warningMessage")}
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-900 dark:text-slate-50">
              {t("workspace.apiKeys.createdDialog.keyName")}
            </label>
            <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-3">
              <p className="text-sm">{apiKeyName}</p>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-900 dark:text-slate-50">
              {t("workspace.apiKeys.createdDialog.accessLevel")}
            </label>
            <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-3">
              <p className="text-sm">{t(`workspace.apiKeys.roles.${role}`)}</p>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-900 dark:text-slate-50">
              {t("workspace.apiKeys.createdDialog.apiKey")}
            </label>
            <div className="relative">
              <textarea
                ref={textareaRef}
                readOnly
                value={apiKey}
                className="w-full rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-3 pr-12 font-mono text-sm resize-none focus:outline-hidden focus:ring-2 focus:ring-slate-950 dark:focus:ring-slate-300"
                rows={3}
                onClick={(e) => e.currentTarget.select()}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-2 top-2"
                onClick={handleCopy}
                title={
                  copied
                    ? t("workspace.apiKeys.createdDialog.copied")
                    : t("workspace.apiKeys.createdDialog.copy")
                }
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>
            {t("workspace.apiKeys.createdDialog.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ApiKeyCreatedDialog
